import { createContext, useContext, useEffect, useState } from "react";
import { en } from "./en";
import { cs } from "./cs";
import type { Translations } from "./en";

export type Language = "en" | "cs";

type LanguageProviderProps = {
  children: React.ReactNode;
  /**
   * The language when nobody has picked one — and, with
   * `followBrowserLanguage`, when the browser names none the interface has.
   */
  defaultLanguage?: Language;
  /**
   * Open in the browser's language when nobody has picked one. The app turns
   * it on (main.tsx); a test that states its language leaves it off, so the
   * machine running it cannot decide which language it reads.
   */
  followBrowserLanguage?: boolean;
  storageKey?: string;
};

type LanguageProviderState = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: Translations;
  locale: string;
};

const translations: Record<Language, Translations> = { en, cs };

const locales: Record<Language, string> = {
  en: "en-US",
  cs: "cs-CZ",
};

const initialState: LanguageProviderState = {
  language: "en",
  setLanguage: () => null,
  t: en,
  locale: "en-US",
};

const LanguageProviderContext =
  createContext<LanguageProviderState>(initialState);

function isLanguage(value: string | null | undefined): value is Language {
  return value === "en" || value === "cs";
}

/**
 * The language the operator picked, or null where nobody has. A browser that
 * blocks site data throws on the read rather than answering null, and that
 * reads as nobody having picked: what speaks while the app is down
 * (`AppUnavailable`) asks through here, and must not go down the same way.
 */
function getStorage(storageKey: string): Language | null {
  try {
    if (typeof window === "undefined" || window.localStorage == null) {
      return null;
    }

    const stored = window.localStorage.getItem(storageKey);
    return isLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * The first of the browser's languages the interface has, by primary subtag
 * ("cs-CZ" is Czech). Everybody used to open in English, so a Czech browser
 * offered to translate the page, and the translation crashed React (Sentry
 * OPTIVO-UI-7/8/A). `index.html` now keeps translators off the page, which
 * makes this the only way a Czech visitor meets Czech before finding the
 * switch.
 *
 * Read, never stored: the stored key is the operator's own choice — the
 * cookie notice calls it that — so writing the detected language there would
 * stop it following the browser.
 */
function getBrowserLanguage(): Language | null {
  if (typeof navigator === "undefined") {
    return null;
  }

  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of preferred) {
    const primary = tag?.split("-")[0]?.toLowerCase();
    if (isLanguage(primary)) {
      return primary;
    }
  }
  return null;
}

function setStorage(storageKey: string, language: Language): void {
  if (typeof window === "undefined" || window.localStorage == null) {
    return;
  }

  window.localStorage.setItem(storageKey, language);
}

const DEFAULT_STORAGE_KEY = "optivo-ui-language";

type OpeningLanguageOptions = Pick<
  LanguageProviderProps,
  "defaultLanguage" | "followBrowserLanguage" | "storageKey"
>;

/**
 * The language the interface opens in: the operator's own choice, then (where
 * the caller follows the browser) the first of the browser's languages the
 * interface has, then the default.
 *
 * The provider opens in it, and so does what stands in for the whole app when
 * it cannot be drawn (`components/AppUnavailable.tsx`). That is drawn above
 * the provider, or with the provider gone, so it cannot ask `useLanguage()`,
 * which outside a provider answers English to everybody. It takes the same
 * options the provider is given, so the two are handed one answer.
 */
export function openingLanguage({
  defaultLanguage = "en",
  followBrowserLanguage = false,
  storageKey = DEFAULT_STORAGE_KEY,
}: OpeningLanguageOptions = {}): Language {
  return getStorage(storageKey)
    ?? (followBrowserLanguage ? getBrowserLanguage() : null)
    ?? defaultLanguage;
}

/** A language's words, for a caller that stands outside the provider. */
export function translationsFor(language: Language): Translations {
  return translations[language];
}

export function LanguageProvider({
  children,
  defaultLanguage = "en",
  followBrowserLanguage = false,
  storageKey = DEFAULT_STORAGE_KEY,
}: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(() =>
    openingLanguage({ defaultLanguage, followBrowserLanguage, storageKey }),
  );

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    setStorage(storageKey, lang);
    setLanguageState(lang);
  };

  const value: LanguageProviderState = {
    language,
    setLanguage,
    t: translations[language],
    locale: locales[language],
  };

  return (
    <LanguageProviderContext.Provider value={value}>
      {children}
    </LanguageProviderContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageProviderContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
